/**
 * Browser Notification & Synthesized Audio Chime Utilities
 * Uses standard Web Notification API and Web Audio API (zero external sound file dependencies).
 */

let audioCtx = null;

/**
 * Plays a gentle, pleasant dual-tone notification chime using the Web Audio API.
 */
export function playNotificationChime() {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    if (!audioCtx || audioCtx.state === "suspended") {
      audioCtx = new AudioContextClass();
    }

    const now = audioCtx.currentTime;

    // Note 1: E5 (659.25 Hz)
    const osc1 = audioCtx.createOscillator();
    const gain1 = audioCtx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(659.25, now);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.15, now + 0.02);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
    osc1.connect(gain1);
    gain1.connect(audioCtx.destination);
    osc1.start(now);
    osc1.stop(now + 0.3);

    // Note 2: B5 (987.77 Hz) - slightly delayed higher harmonic chime
    const osc2 = audioCtx.createOscillator();
    const gain2 = audioCtx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(987.77, now + 0.08);
    gain2.gain.setValueAtTime(0, now + 0.08);
    gain2.gain.linearRampToValueAtTime(0.2, now + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
    osc2.connect(gain2);
    gain2.connect(audioCtx.destination);
    osc2.start(now + 0.08);
    osc2.stop(now + 0.48);
  } catch (err) {
    // Non-blocking fallback for browsers blocking audio before first interaction
    console.debug("[browserNotification] Chime prevented:", err);
  }
}

/**
 * Checks current browser notification permission status.
 * @returns {'granted' | 'denied' | 'default' | 'unsupported'}
 */
export function getNotificationPermissionStatus() {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  return Notification.permission;
}

/**
 * Requests browser permission to show desktop notifications.
 * @returns {Promise<'granted' | 'denied' | 'default' | 'unsupported'>}
 */
export async function requestBrowserNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (err) {
    console.error("[browserNotification] Permission request failed:", err);
    return "denied";
  }
}

/**
 * Sends a native desktop notification and optionally plays a sound chime.
 * @param {string} title
 * @param {object} options
 * @param {string} [options.body]
 * @param {string} [options.icon]
 * @param {string} [options.tag]
 * @param {boolean} [options.playSound=true]
 * @param {Function} [options.onClick]
 */
export function sendBrowserNotification(title, options = {}) {
  const {
    body = "",
    icon = "/favicon.ico",
    tag,
    playSound = true,
    onClick,
  } = options;

  if (playSound) {
    playNotificationChime();
  }

  if (typeof window === "undefined" || !("Notification" in window)) {
    return null;
  }

  if (Notification.permission !== "granted") {
    return null;
  }

  try {
    const notification = new Notification(title, {
      body,
      icon,
      tag: tag || `korvry-${Date.now()}`,
      badge: icon,
      silent: true, // we handle our own gentle chime
    });

    notification.onclick = function (event) {
      event.preventDefault();
      window.focus();
      if (typeof onClick === "function") {
        onClick(notification);
      }
      notification.close();
    };

    return notification;
  } catch (err) {
    console.error("[browserNotification] Failed to create Notification:", err);
    return null;
  }
}
